import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch, RootState } from '../../../../store/store';
import { useParams } from 'react-router-dom';
import { changeFaultControlAction } from './faultControlActions';

// Faults matter more than most other polled data, so this page refreshes
// faster than the shared EXTENDED_INTERVAL_UPDATE_DATA (30s) cadence.
const FAULT_CONTROL_POLL_INTERVAL_MS = 15000;

type ComponentProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

const FaultControl: React.FC<ComponentProps> = ({
  faultControl,
  faultControlError,
  getFaultControl,
}) => {
  const { id } = useParams();
  const [hasLoaded, setHasLoaded] = useState(false);

  const updateItemHandler = useCallback(
    (setup_idx: number, setup_item_idx: number, value: boolean) => {
      const setup = faultControl[setup_idx];
      const item = setup.items[setup_item_idx];
      changeFaultControlAction(id, setup.command, item.command, value);
    },
    [faultControl, id]
  );

  useEffect(() => {
    const fetchFaultControl = async () => {
      await getFaultControl(id);
      setHasLoaded(true);
    };

    fetchFaultControl();
    const intervalId = setInterval(fetchFaultControl, FAULT_CONTROL_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [id]);

  // Every fault setup shares this same 5-item order (see
  // faultControlSetupValueList on the backend), so it's safe to render one
  // fixed set of columns and index straight into each setup's items array.
  const columns = [
    { label: 'Trips Shunt', hint: 'This fault trips the shunt' },
    { label: 'Pauses Master/Slave', hint: 'Pauses the master slave chain' },
    { label: 'Clears on Fix', hint: 'Clears itself if the problem is fixed' },
    { label: 'Shows When Channel Off', hint: 'Appears even if the channel is not running' },
  ];

  return (
    <div className="p-edit-controller-fault-control pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">
            Edit Fault Control
          </h3>
        </div>
        {faultControlError && (
          <div className="mx-7 mt-4 whitespace-pre-wrap rounded-sm border border-danger bg-danger/10 p-4 text-sm text-danger">
            <p className="font-medium">Failed to load fault control data:</p>
            <p className="mt-1">{faultControlError}</p>
          </div>
        )}
        {!faultControlError && hasLoaded && faultControl.length === 0 && (
          <div className="mx-7 mt-4 rounded-sm border border-warning bg-warning/10 p-4 text-sm text-black dark:text-white">
            No fault control data is currently available for this controller. The request succeeded but returned
            zero fault types.
          </div>
        )}
        {!faultControlError && faultControl.length > 0 && (
          <div className="overflow-x-auto p-7 pt-4">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-primary text-left text-xs uppercase leading-5 text-white">
                  <th className="whitespace-nowrap py-2 pl-4">Fault</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center" title="Enable this fault?">
                    Enabled
                  </th>
                  {columns.map((col) => (
                    <th
                      key={col.label}
                      className="whitespace-nowrap px-2 py-2 text-center"
                      title={col.hint}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {faultControl.map((setup, idx) => {
                  const isEnabled = !!setup.items[0]?.status;
                  return (
                    <tr
                      key={setup.command}
                      className={`border-b border-stroke text-sm dark:border-strokedark ${
                        idx % 2 === 0 ? 'bg-white dark:bg-boxdark' : 'bg-gray-2 dark:bg-meta-4'
                      }`}
                    >
                      <td className="whitespace-nowrap py-2 pl-4 font-medium text-black dark:text-white">
                        <span className="flex items-center gap-2">
                          <span
                            className={`h-2.5 w-2.5 rounded-full ${
                              isEnabled ? 'bg-meta-3' : 'bg-gray-4 dark:bg-meta-4'
                            }`}
                          />
                          {setup.name}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4.5 w-4.5 cursor-pointer accent-primary"
                          checked={isEnabled}
                          onChange={(event) =>
                            updateItemHandler(idx, 0, event.target.checked)
                          }
                        />
                      </td>
                      {setup.items.slice(1).map((item, itemIdx) => (
                        <td key={item.command} className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            className="h-4.5 w-4.5 cursor-pointer accent-primary"
                            checked={!!item.status}
                            onChange={(event) =>
                              updateItemHandler(idx, itemIdx + 1, event.target.checked)
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  faultControl: state.controllers.fault_control,
  faultControlError: state.controllers.fault_control_error,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getFaultControl: dispatch.controllers.getControllerFaultControl,
});

export default connect(mapState, mapDispatch)(FaultControl);
